import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Avatar,
  TextField, Divider, Stack, Chip, CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/auth.store';
import { useToast, TOAST_TYPES } from '../../providers/ToastProvider';
import { TickCircle, CloseCircle } from 'iconsax-react';
import {
  getGoogleStatus, connectGoogle, disconnectGoogle,
  updateProfile, updatePassword,
} from '../../services/settings.service';

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { showToast } = useToast();
  const location = useLocation();

  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEmail,     setGoogleEmail]     = useState<string | null>(null);
  const [googleLoading,   setGoogleLoading]   = useState(false);

  const [name,            setName]            = useState(user?.name || '');
  const [currentPw,       setCurrentPw]       = useState('');
  const [newPw,           setNewPw]           = useState('');
  const [confirmPw,       setConfirmPw]       = useState('');
  const [saving,          setSaving]          = useState(false);

  const oauthWindowRef = useRef<Window | null>(null);

  const handleOAuthCode = async (code: string) => {
    setGoogleLoading(true);
    try {
      const res: any = await connectGoogle(code);
      setGoogleConnected(true);
      setGoogleEmail(res.email ?? null);
      showToast(t('settings.googleConnectedToast'), TOAST_TYPES.SUCCESS);
    } catch (err: any) {
      showToast(err?.response?.data?.detail || t('settings.googleConnectError'), TOAST_TYPES.ERROR);
    } finally {
      setGoogleLoading(false);
    }
  };

  // ── Fetch Google status on mount ──────────────────────────────
  useEffect(() => {
    getGoogleStatus()
      .then((res: any) => {
        setGoogleConnected(res.connected ?? false);
        setGoogleEmail(res.email ?? null);
      })
      .catch(() => {});
  }, []);

  // ── Handle redirect-based OAuth (non-popup fallback) ─────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('google_code');
    if (code) {
      handleOAuthCode(code);
      window.history.replaceState({}, '', window.location.pathname);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Listen for OAuth callback code via popup message ──────────
  useEffect(() => {
    const handler = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'google_oauth_code' && event.data.code) {
        oauthWindowRef.current?.close();
        await handleOAuthCode(event.data.code);
      } else if (event.data?.type === 'google_oauth_error') {
        showToast(t('settings.googleConnectError'), TOAST_TYPES.ERROR);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast, t]);

  // ── Connect Google ────────────────────────────────────────────
  const handleGoogleConnect = async () => {
    setGoogleLoading(true);
    try {
      const res: any = await getGoogleStatus();
      console.log('[Google OAuth] /settings/google response:', res);
      // support different field names the backend might use
      const authUrl: string = res.auth_url || res.oauth_url || res.authorization_url || res.url;
      if (!authUrl) {
        console.error('[Google OAuth] No auth URL in response. Full response:', res);
        showToast('الباك إند ما أرسل رابط OAuth — تأكد إن /settings/google يرجع auth_url', TOAST_TYPES.ERROR);
        return;
      }
      const popup = window.open(authUrl, 'google_oauth', 'width=500,height=620,left=400,top=100');
      oauthWindowRef.current = popup;
    } catch (err: any) {
      showToast(err?.response?.data?.detail || t('settings.googleConnectError'), TOAST_TYPES.ERROR);
    } finally {
      setGoogleLoading(false);
    }
  };

  // ── Disconnect Google ─────────────────────────────────────────
  const handleGoogleDisconnect = async () => {
    setGoogleLoading(true);
    try {
      await disconnectGoogle();
      setGoogleConnected(false);
      setGoogleEmail(null);
      showToast(t('settings.googleDisconnectedToast'), TOAST_TYPES.SUCCESS);
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Failed to disconnect', TOAST_TYPES.ERROR);
    } finally {
      setGoogleLoading(false);
    }
  };

  // ── Save profile ──────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({ name });
      showToast(t('settings.savedSuccess'), TOAST_TYPES.SUCCESS);
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Failed to save', TOAST_TYPES.ERROR);
    } finally {
      setSaving(false);
    }
  };

  // ── Change password ───────────────────────────────────────────
  const handleChangePassword = async () => {
    if (newPw !== confirmPw) {
      showToast(t('settings.passwordMismatch'), TOAST_TYPES.ERROR);
      return;
    }
    setSaving(true);
    try {
      await updatePassword({ current_password: currentPw, new_password: newPw });
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      showToast(t('settings.savedSuccess'), TOAST_TYPES.SUCCESS);
    } catch (err: any) {
      showToast(err?.response?.data?.detail || 'Failed to change password', TOAST_TYPES.ERROR);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 680, mx: 'auto' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {t('settings.title')}
        </Typography>
      </Box>

      <Stack spacing={3}>

        {/* ── Profile ── */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 3 }}>
              {t('settings.profileTitle')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Avatar sx={{ width: 56, height: 56, background: 'linear-gradient(135deg, #2563EB, #4F46E5)', fontSize: '1.4rem', fontWeight: 700 }}>
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>{user?.name?.split(' ')[0]}</Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{user?.email}</Typography>
              </Box>
            </Box>

            <Stack spacing={2.5}>
              <TextField
                label={t('settings.fullName')}
                value={name}
                onChange={e => setName(e.target.value)}
                fullWidth size="small"
              />
              <TextField
                label={t('settings.emailAddress')}
                defaultValue={user?.email}
                fullWidth size="small" disabled
              />
            </Stack>

            <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ mt: 3 }}>
              {saving ? <CircularProgress size={18} color="inherit" /> : t('settings.saveChanges')}
            </Button>

          </CardContent>
        </Card>

        {/* ── Google Workspace ── */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: googleConnected ? 2.5 : 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ width: 48, height: 48, borderRadius: 2, border: '1.5px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fff', flexShrink: 0 }}>
                  <svg width="24" height="24" viewBox="0 0 48 48">
                    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
                    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7z"/>
                  </svg>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>Google Account</Typography>
                  <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary', mt: 0.25 }}>
                    {t('settings.googleDesc')}
                  </Typography>
                </Box>
              </Box>

              <Chip
                icon={googleConnected
                  ? <TickCircle size={13} color="#16A34A" />
                  : <CloseCircle size={13} color="#6B7280" />
                }
                label={googleConnected ? t('settings.connected') : t('settings.notConnected')}
                size="small"
                sx={googleConnected
                  ? { bgcolor: '#F0FDF4', color: '#16A34A', fontWeight: 600, border: '1px solid #BBF7D0' }
                  : { bgcolor: '#F9FAFB', color: '#6B7280', fontWeight: 500, border: '1px solid #E5E7EB' }
                }
              />
            </Box>

            {googleConnected && (
              <Box sx={{ p: 2, borderRadius: 2, background: 'linear-gradient(135deg, #F0FDF4, #ECFDF5)', border: '1px solid #BBF7D0', mb: 2.5 }}>
                <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#15803D', mb: 0.5 }}>
                  {t('settings.connectedAs', { email: googleEmail || user?.email })}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#166534' }}>
                  {t('settings.connectedDesc')}
                </Typography>
              </Box>
            )}

            {googleConnected ? (
              <Button
                variant="outlined" size="small"
                onClick={handleGoogleDisconnect}
                disabled={googleLoading}
                sx={{ borderColor: '#FCA5A5', color: '#DC2626', '&:hover': { bgcolor: '#FEF2F2', borderColor: '#FCA5A5' } }}
              >
                {googleLoading ? <CircularProgress size={16} color="inherit" /> : t('settings.disconnect')}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleGoogleConnect}
                disabled={googleLoading}
                startIcon={!googleLoading && (
                  <svg width="16" height="16" viewBox="0 0 48 48">
                    <path fill="#fff" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                    <path fill="#fff" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                    <path fill="#fff" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
                    <path fill="#fff" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7z"/>
                  </svg>
                )}
                sx={{ bgcolor: '#4285F4', '&:hover': { bgcolor: '#3367D6' }, backgroundImage: 'none' }}
              >
                {googleLoading ? <CircularProgress size={18} color="inherit" /> : t('settings.connectGoogle')}
              </Button>
            )}
          </CardContent>
        </Card>

      </Stack>
    </Box>
  );
}
